import { API_BASE } from '../config.js';
import type {
  RaceHistoryDetail,
  RaceHistoryListResponse,
  SaveRaceHistoryBody,
} from '@multirx/shared/types/raceHistory.js';

export const ARCHIVE_LIST_PAGE_SIZE = 20;

export async function fetchRaceHistoriesPage(
  limit = ARCHIVE_LIST_PAGE_SIZE,
  offset = 0,
  search = ''
): Promise<RaceHistoryListResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (search.trim()) params.set('search', search.trim());
  const res = await fetch(`${API_BASE}/api/race-history?${params.toString()}`);
  if (!res.ok) throw new Error('Erreur lors du chargement des replays');
  const raw: unknown = await res.json();
  let page = normalizeRaceHistoryListResponse(raw, limit, offset);

  // API legacy : tableau tronqué à `limit` — le total réel vient de /count.
  const looksTruncated =
    Array.isArray(raw) && raw.length === limit && offset === 0 && page.total <= limit;
  const needsTotal =
    page.items.length === limit &&
    (looksTruncated || (page.hasMore && page.total <= offset + page.items.length));

  if (needsTotal || (page.hasMore && page.total === page.items.length + offset)) {
    const count = await fetchPlayedRacesCount().catch(() => page.total);
    if (count > page.total) {
      page = {
        ...page,
        total: count,
        hasMore: offset + page.items.length < count,
      };
    }
  }

  return page;
}

/** Compat : ancienne API (tableau) et nouvelle API paginée ({ items, total, hasMore }). */
export function normalizeRaceHistoryListResponse(
  raw: unknown,
  limit: number,
  offset: number
): RaceHistoryListResponse {
  if (Array.isArray(raw)) {
    const items = raw.slice(offset, offset + limit) as RaceHistoryListResponse['items'];
    return {
      items,
      total: raw.length,
      hasMore: offset + items.length < raw.length,
    };
  }
  if (raw && typeof raw === 'object') {
    const r = raw as Partial<RaceHistoryListResponse>;
    const items = Array.isArray(r.items) ? r.items : [];
    const total = typeof r.total === 'number' ? r.total : items.length + offset;
    return {
      items,
      total,
      hasMore: typeof r.hasMore === 'boolean' ? r.hasMore : offset + items.length < total,
    };
  }
  return { items: [], total: 0, hasMore: false };
}

export async function fetchRaceHistory(id: number): Promise<RaceHistoryDetail> {
  const res = await fetch(`${API_BASE}/api/race-history/${id}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Replay introuvable');
  }
  return res.json() as Promise<RaceHistoryDetail>;
}

export async function saveRaceHistory(body: SaveRaceHistoryBody): Promise<RaceHistoryDetail> {
  const res = await fetch(`${API_BASE}/api/race-history`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? "Erreur lors de l'enregistrement");
  }
  return res.json() as Promise<RaceHistoryDetail>;
}

export async function deleteRaceHistory(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/api/race-history/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? 'Erreur lors de la suppression');
  }
}

export async function uploadRaceHistoryPreview(
  id: number,
  file: Blob,
  filename = 'preview.png'
): Promise<RaceHistoryDetail> {
  const formData = new FormData();
  formData.append('image', file, filename);
  const res = await fetch(`${API_BASE}/api/race-history/${id}/preview`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error ?? "Erreur lors de l'upload de la vignette");
  }
  return res.json() as Promise<RaceHistoryDetail>;
}

export async function fetchPlayedRacesCount(): Promise<number> {
  const res = await fetch(`${API_BASE}/api/race-history/count`);
  if (!res.ok) throw new Error('Erreur lors du comptage des parties');
  const data = (await res.json()) as { count: number };
  return data.count;
}
